import React, { useEffect, useState } from 'react';
import { getRiskLog, getTradovateHealth, getHealth } from '../api';
import axios from 'axios';
import { ClassBadge, EntryModeBadge, QualityScore } from './ClassBadge';

const relativeTime = (iso: string | null | undefined) => {
  if (!iso) return 'never';
  const then = new Date(iso).getTime();
  if (isNaN(then)) return 'never';
  const diff = Math.max(0, Date.now() - then);
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  return `${h}h ago`;
};

function BrokerHealthCard() {
  const [health, setHealth] = useState<any>(null);
  const [paperBalance, setPaperBalance] = useState<number | null>(null);
  const [startingBalance, setStartingBalance] = useState<number>(0);
  const [, setTick] = useState(0);

  useEffect(() => {
    const load = async () => {
      try {
        const [r, h] = await Promise.all([getTradovateHealth(), getHealth()]);
        setHealth(r.data);
        setPaperBalance(h.data?.account_balance ?? null);
        setStartingBalance(h.data?.account_starting_balance ?? 0);
      } catch (e: any) {
        setHealth({ connected: false, error: e?.message || 'Request failed', last_checked: new Date().toISOString() });
      }
    };
    load();
    const poll = setInterval(load, 30000);
    const tick = setInterval(() => setTick(t => t + 1), 1000);
    return () => { clearInterval(poll); clearInterval(tick); };
  }, []);

  if (!health) {
    return (
      <div className="bg-gray-900 rounded-lg border border-gray-800 p-4">
        <div className="text-sm font-semibold text-gray-400 uppercase tracking-wide">Paper Account — Tradovate Ready</div>
        <div className="text-xs text-gray-600 mt-2">Loading…</div>
      </div>
    );
  }

  const connected = !!health.connected;
  const env = (health.env || 'demo').toLowerCase();
  const isLive = env === 'live';
  const envLabel = env.charAt(0).toUpperCase() + env.slice(1);
  const realized = Number(health.realized_pnl || 0);
  const open = Number(health.open_pnl || 0);
  const balance = Number(health.cash_balance || 0);
  const balanceLabel = isLive ? 'Balance' : 'Demo Balance';

  return (
    <div className="space-y-4">
      <div className="mb-1"><h1 className="text-xl font-bold">Risk</h1><p className="text-xs text-gray-500 mt-0.5">Risk monitoring. Daily loss limits, position sizing rules, and current exposure.</p></div>
      <div className="bg-gray-900 rounded-lg border border-gray-800 p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm font-semibold text-gray-400 uppercase tracking-wide">
          {isLive ? 'Tradovate Live Connection' : 'Paper Account — Tradovate Ready'}
        </div>
        <div className="flex items-center gap-2">
          <span className={`w-2 h-2 rounded-full ${connected ? 'bg-green-400' : 'bg-red-400'}`} />
          <span className={`text-xs ${connected ? 'text-green-400' : 'text-red-400'}`}>
            {connected ? `Connected to ${envLabel}` : 'Disconnected'}
          </span>
          {connected && (
            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wide ${
              isLive ? 'bg-green-900 text-green-300 border border-green-700' : 'bg-gray-800 text-gray-400 border border-gray-700'
            }`}>
              {isLive ? 'Live' : 'Demo'}
            </span>
          )}
        </div>
      </div>
      {!connected && health.error && (
        <div className="text-xs text-red-400 mb-2">{health.error}</div>
      )}
      {health.account_name && (
        <div className="text-xs text-gray-500 mb-1">Account: {health.account_name}</div>
      )}
      <div className="grid grid-cols-3 gap-3 mt-2">
        <div>
          <div className="text-xs text-gray-500 uppercase">{isLive ? 'Live Balance' : 'Demo Balance'}</div>
          <div className="text-2xl font-bold font-mono text-white">
            {balance > 0 ? `$${balance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—'}
          </div>
        </div>
        <div>
          <div className="text-xs text-gray-500 uppercase">Realized P&L</div>
          <div className={`text-lg font-bold font-mono ${realized >= 0 ? 'text-green-400' : 'text-red-400'}`}>${realized.toFixed(2)}</div>
        </div>
        <div>
          <div className="text-xs text-gray-500 uppercase">Open P&L</div>
          <div className={`text-lg font-bold font-mono ${open >= 0 ? 'text-green-400' : 'text-red-400'}`}>${open.toFixed(2)}</div>
        </div>
      </div>
      <div className="text-xs text-gray-500 mt-3">Last checked {relativeTime(health.last_checked)}</div>
    </div>
    </div>
  );
}

export default function RiskPanel() {
  const [riskLog, setRiskLog] = useState<any[]>([]);
  const [health, setHealth] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [killSwitchActive, setKillSwitchActive] = useState(false);
  const [eventFilter, setEventFilter] = useState<string>('all');
  const [classFilter, setClassFilter] = useState<string>('all');
  const [blockedFilter, setBlockedFilter] = useState<string>('all');
  const [sortField, setSortField] = useState<string>('created_at');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const handleSort = (f: string) => { if (sortField === f) setSortDir(sortDir === 'asc' ? 'desc' : 'asc'); else { setSortField(f); setSortDir('desc'); } };
  const SortHeader = ({ field, label }: { field: string; label: string }) => (
    <th className="text-left p-3 cursor-pointer hover:text-white select-none" onClick={() => handleSort(field)}>
      {label} {sortField === field && <span className="text-blue-400">{sortDir === 'asc' ? '↑' : '↓'}</span>}
    </th>
  );

  const filteredLogRaw = riskLog.filter((r: any) =>
    (eventFilter === 'all' || r.event === eventFilter) &&
    (classFilter === 'all' || r.signal_class === classFilter) &&
    (blockedFilter === 'all' || (blockedFilter === 'yes' ? r.blocked : !r.blocked))
  );
  const filteredLog = [...filteredLogRaw].sort((a, b) => {
    const av = a[sortField]; const bv = b[sortField];
    if (av == null) return 1; if (bv == null) return -1;
    const cmp = typeof av === 'number' && typeof bv === 'number' ? av - bv : String(av).localeCompare(String(bv));
    return sortDir === 'asc' ? cmp : -cmp;
  });

  useEffect(() => {
    const load = async () => {
      try {
        const [r, h] = await Promise.all([getRiskLog(), getHealth()]);
        setRiskLog(r.data);
        setHealth(h.data);
        setKillSwitchActive(r.data[0]?.event === 'kill_switch_activated');
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    load();
    const interval = setInterval(load, 5000);
    return () => clearInterval(interval);
  }, []);

  const handleKillSwitch = async () => {
    if (!window.confirm('Activate kill switch? This will halt all trading.')) return;
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/risk/kill-switch`);
      setKillSwitchActive(true);
    } catch (e) { console.error(e); }
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-gray-500">Loading...</div>;

  return (
    <div className="space-y-4">
      <BrokerHealthCard />

      <div className="bg-gray-900 rounded-lg border border-gray-800 p-4">
        <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-3">Daily Loss Limit</h3>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <div className="text-xs text-gray-500 uppercase">Limit (Dollars)</div>
            <div className="text-2xl font-bold font-mono text-white">${(riskLog[0]?.daily_loss_limit || 500).toFixed(0)}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500 uppercase">Starting Balance</div>
            <div className="text-2xl font-bold font-mono text-white">${(health?.account_starting_balance || 0).toFixed(0)}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500 uppercase">Limit % of Start</div>
            <div className="text-2xl font-bold font-mono text-blue-400">
              {health?.account_starting_balance > 0 ? ((riskLog[0]?.daily_loss_limit || 500) / health.account_starting_balance * 100).toFixed(1) : '—'}%
            </div>
          </div>
        </div>
      </div>

      <div className="bg-gray-900 rounded-lg border border-gray-800">
        <div className="p-4 border-b border-gray-800">
          <div>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <h2 className="font-semibold">Risk Event Log</h2>
              <div className="flex gap-2 flex-wrap">
                <select value={eventFilter} onChange={(e) => setEventFilter(e.target.value)} className="bg-gray-800 border border-gray-700 rounded px-3 py-1 text-xs">
                  <option value="all">All Events</option>
                  <option value="trade_approved">Approved</option>
                  <option value="trade_blocked">Blocked</option>
                  <option value="kill_switch_activated">Kill Switch</option>
                  <option value="mtf_trend_conflict_block">MTF Trend Block</option>
                  <option value="mtf_trend_gate_eval">MTF Gate Eval</option>
                  <option value="shadow_block">Shadow Block</option>
                  <option value="second_chance_armed">Second Chance Armed</option>
                </select>
                <select value={classFilter} onChange={(e) => setClassFilter(e.target.value)} className="bg-gray-800 border border-gray-700 rounded px-3 py-1 text-xs">
                  <option value="all">All Classes</option>
                  <option value="SMR">SMR</option>
                  <option value="MACRO_LIQ">MACRO_LIQ</option>
                  <option value="MACRO_ONLY">MACRO_ONLY</option>
                  <option value="BREAKER_ONLY">BREAKER_ONLY</option>
                  <option value="TREND_CONT">TREND_CONT</option>
                  <option value="SECOND_CHANCE">SECOND_CHANCE</option>
                  <option value="BIAS_SNIPE">BIAS_SNIPE (shadow)</option>
                  <option value="DAILY_TREND">DAILY_TREND (shadow)</option>
                </select>
                <select value={blockedFilter} onChange={(e) => setBlockedFilter(e.target.value)} className="bg-gray-800 border border-gray-700 rounded px-3 py-1 text-xs">
                  <option value="all">All Status</option>
                  <option value="yes">Blocked</option>
                  <option value="no">Allowed</option>
                </select>
              </div>
            </div>
            <div className="flex items-center gap-3 text-xs text-gray-600 mt-1">
              <span>Sizing is criteria-count based (4 boolean gate criteria). Score-threshold gates are retired; scores shown are informational only.</span>
            </div>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-gray-500 text-xs uppercase border-b border-gray-800">
                <SortHeader field="created_at" label="Time" />
                <SortHeader field="signal_class" label="Class" />
                <SortHeader field="quality_score" label="Score" />
                <SortHeader field="exit_strategy" label="Exit" />
                <SortHeader field="instrument" label="Instrument" />
                <SortHeader field="daily_loss_used" label="Daily Loss Used" />
                <SortHeader field="positions_open" label="Positions" />
                <SortHeader field="blocked" label="Blocked" />
                <SortHeader field="event" label="Event" />
                <th className="text-left p-3">Reason</th>
              </tr>
            </thead>
            <tbody>
              {filteredLog.length === 0 ? (
                <tr><td colSpan={11} className="text-center text-gray-600 p-8">No risk events yet</td></tr>
              ) : filteredLog.map((r: any) => (
                <tr key={r.id} className="border-b border-gray-800 hover:bg-gray-800 transition-colors">
                  <td className="p-3 text-gray-400 text-xs">{new Date(r.created_at).toLocaleString()}</td>
                  <td className="p-3 whitespace-nowrap"><ClassBadge signalClass={r.signal_class} /><EntryModeBadge mode={r.entry_mode} /></td>
                  <td className="p-3"><QualityScore score={r.quality_score} /></td>
                  <td className="p-3"><span className={`text-xs px-1.5 py-0.5 rounded ${r.exit_strategy === 'simple' ? 'bg-blue-900 text-blue-300' : r.exit_strategy === 'scaled' ? 'bg-purple-900 text-purple-300' : 'bg-gray-700 text-gray-500'}`}>{r.exit_strategy || '—'}</span></td>
                  <td className="p-3">{r.instrument || '—'}</td>
                  <td className="p-3 font-mono text-xs">${r.daily_loss_used?.toFixed(2) || '0.00'} / ${r.daily_loss_limit?.toFixed(2) || '0.00'}</td>
                  <td className="p-3">{r.positions_open}/{r.positions_limit}</td>
                  <td className="p-3">{r.blocked ? <span className="text-red-400">Yes</span> : <span className="text-green-400">No</span>}</td>
                  <td className="p-3 font-medium">{r.event}</td>
                  <td className="p-3 text-xs text-gray-400 max-w-md">
                    {r.details?.reasons?.length > 0 ? r.details.reasons.join('; ') : (r.details?.reason || '—')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}